// Copyright 2025-2026 Florian Gallon. All Rights reserved.
import { ChefHat, Store, Shield, Calculator, MapPin, Calendar, Check } from 'lucide-react';

export default function Features({ isFr }) {
  const features = [
    {
      icon: ChefHat,
      title: isFr ? 'Votre assistant boulangerie complet' : 'Your complete bakery assistant',
      body: isFr
        ? 'Gérez production, commandes, clients, recettes, stock et ventes — tout au même endroit.\nArrêtez de jongler entre carnets, tableurs et calculatrices.'
        : 'Manage production, orders, customers, recipes, stock and sales — all in one place.\nStop juggling notebooks, spreadsheets and calculators.',
      note: isFr ? 'Remplace cinq outils par un seul' : 'Replaces five tools with one',
    },
    {
      icon: Store,
      title: isFr ? 'Boutique en ligne professionnelle incluse' : 'Professional online shop included',
      body: isFr
        ? 'Votre boutique en ligne est incluse à votrenom.digi-bakery.com — rien d’autre à construire ni à payer.\nVos clients commandent 24h/24, paiement Stripe intégré.\nPas de frais de configuration, pas de commission.'
        : 'Your online shop is included at yourname.digi-bakery.com — nothing else to build or pay for.\nCustomers order around the clock, with Stripe payment built in.\nNo setup fees, no platform commission.',
      note: isFr ? 'Vendez en ligne immédiatement' : 'Sell online right away',
    },
    {
      icon: Shield,
      title: isFr ? 'Traçabilité complète et sécurité alimentaire' : 'Complete traceability and food safety',
      body: isFr
        ? 'Suivez chaque ingrédient, chaque fournée, chaque vente.\nGestion des stocks avec traçabilité automatique.\nVous savez ce que vous avez cuit, quand, et avec quels numéros de lot.'
        : 'Track every ingredient, every batch, every sale.\nStock management with automatic traceability records.\nYou know what you baked, when, and with which lot numbers.',
      note: isFr ? 'Prêt pour l’audit, exportable en un clic' : 'Audit-ready, exportable in one click',
    },
    {
      icon: Calculator,
      title: isFr ? 'Votre savoir-faire, pas des calculs' : 'Your craft, not calculations',
      body: isFr
        ? 'Définissez vos recettes une fois.\nDigi-Bakery adapte les quantités à vos commandes.\nGagnez du temps, restez au fournil.'
        : 'Define your recipes once.\nDigi-Bakery scales the quantities to your orders.\nSave time, stay in the bakery.',
      note: isFr ? 'Des heures gagnées chaque semaine' : 'Hours saved every week',
    },
    {
      icon: MapPin,
      title: isFr ? 'Vendez partout, à votre façon' : 'Sell anywhere, your way',
      body: isFr
        ? 'Plusieurs points de vente, avec des produits et des horaires de retrait différents à chacun.\nMarché le lundi, retrait à domicile le mercredi, boutique le samedi — votre boutique en ligne s’ajuste, vos commandes restent en ordre.'
        : 'Multiple selling points, each with its own products and pickup times.\nMarket on Monday, home pickup on Wednesday, shop on Saturday — your online shop adjusts and your orders stay in order.',
      note: isFr ? 'Fonctionne pour tout type de boulanger' : 'Works for any kind of baker',
    },
    {
      icon: Calendar,
      title: isFr ? 'Produisez l’esprit tranquille' : 'Produce with peace of mind',
      body: isFr
        ? 'Digi-Bakery s’appuie sur vos commandes ou vos volumes prévus pour sécuriser ingrédients et quantités.\nMoins de vérifications, moins de pertes, plus de temps au fournil.'
        : 'Digi-Bakery uses your orders or planned volumes to secure ingredients and quantities.\nLess checking, less waste, more time in the bakery.',
      note: isFr ? 'Visuel, simple, efficace' : 'Visual, simple, effective',
    },
  ];

  return (
    <section id="features" className="dbk-section dbk-section--line">
      <div className="dbk-wrap">
        <div className="dbk-head">
          <h2 className="dbk-h2">
            {isFr ? 'Un seul outil pour toute l’activité' : 'One tool for the whole operation'}
          </h2>
          <p className="dbk-lede">
            {isFr
              ? 'De la planification de production aux ventes en ligne, gérez chaque aspect de votre boulangerie sur une seule plateforme.'
              : 'From production planning to online sales, run every part of your bakery on a single platform.'}
          </p>
        </div>

        <div className="dbk-features">
          {features.map(({ icon: Icon, title, body, note }) => (
            <article className="dbk-feature" key={title}>
              <h3 className="dbk-h3 dbk-feature__title">
                <Icon size={20} strokeWidth={1.75} />
                {title}
              </h3>
              <p className="dbk-feature__body">{body}</p>
              <p className="dbk-feature__note">
                <Check size={15} strokeWidth={2.25} />
                {note}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
